<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { CourseSubViewType, KickoffHintViewed } from "~/enums/course.enums";
import { KickoffMasteryTrackingEvents, KickoffTabHitCountKey } from "~/enums/tracking/kickoff-mastery.enums";

const props = defineProps<{
  currentActivityId: string | undefined;
}>();

const route = useRoute();
const {
  course,
  getActivity,
  subView,
  isKickoffCompleted,
} = useCourseState();
const { dispatchTrackingEvent } = useTracking();

enum SidebarViewType {
  Course = 'course',
  Resources = 'resources',
  Preferences = 'preferences',
  KickOff = 'kick-off',
}
const sidebarActiveView = ref<SidebarViewType.Course | SidebarViewType.Resources | SidebarViewType.Preferences | SidebarViewType.KickOff>(SidebarViewType.Course);

const currentActivityHasUnseenResources = computed(() => {
  // only lessons have resources
  if (!route.params.lessonId || !props.currentActivityId) {
    return;
  }

  const activity = getActivity(props.currentActivityId);
  if (!activity) {
    return;
  }

  return Array.isArray(activity.learningResourceDownloads) && activity.learningResourceDownloads.length > 0;
});

const handleTabClick = (view: SidebarViewType) => {
  if (view === SidebarViewType.KickOff) {
    let kickoffTabHitCount = Number(localStorage.getItem(KickoffTabHitCountKey)) || 0;
    kickoffTabHitCount += 1;
    localStorage.setItem(KickoffTabHitCountKey, kickoffTabHitCount.toString());

    if (kickoffTabHitCount > 1) {
      dispatchTrackingEvent(KickoffMasteryTrackingEvents.KickoffTabOpened);
    }
  }

  sidebarActiveView.value = view;
};

/*
 * kick-off tab hint
 */
const showKickOffTabHint = ref(false);

watch(() => isKickoffCompleted.value, (newValue, oldValue) => {
  if (newValue === true && oldValue === false && localStorage.getItem(KickoffHintViewed) !== 'true') {
    nextTick(() => {
      showKickOffTabHint.value = true;
    });
  }
});

watch(() => showKickOffTabHint.value, (newValue, oldValue) => {
  if (oldValue === true && newValue === false) {
    localStorage.setItem(KickoffHintViewed, 'true');
  }
});
</script>

<template>
  <aside
    class="sidebar h-full bg-neutral-10 overflow-y-scroll transition-all"
    :class="sidebarActiveView === SidebarViewType.KickOff
      ? 'lg:w-[560px] w-full'
      : subView !== CourseSubViewType.Course && course
        ? 'lg:w-[410px] w-full'
        : 'w-[0px]'"
  >
    <div class="sticky top-0 bg-white z-[900] flex flex-col lg:!h-[72px] !h-[50px] md-h:!h-[50px]">
      <!-- fixme: refactor using other tabs, not mui ones -->
      <div class="flex items-center gap-4 pl-6 pr-4">
        <div class="flex justify-start gap-6 w-full">
          <MButtonNavigation
            :active="sidebarActiveView === SidebarViewType.Course"
            class="lg:!text-14 !text-12 !px-0 lg:h-[72px] h-[50px] md-h:h-[50px] uppercase"
            @click.prevent="handleTabClick(SidebarViewType.Course)"
          >
            {{course?.type}}
          </MButtonNavigation>

          <MButtonNavigation
            v-if="course?.learningResources && course?.learningResources?.length > 0"
            :active="sidebarActiveView === 'resources'"
            class="lg:!text-14 !text-12 !px-0 lg:h-[72px] h-[50px] md-h:h-[50px] uppercase"
            @click.prevent="handleTabClick(SidebarViewType.Resources)"
          >
            <div class="relative">
              Resources
              <Badge
                v-if="currentActivityHasUnseenResources"
                class="absolute -top-2 bg-blue-60"
                severity="info"
              />
            </div>
          </MButtonNavigation>

          <MButtonNavigation
            v-if="isKickoffCompleted && course?.kickoffAndMasteryQuizzesEnabled"
            id="kick-off-tab"
            :active="sidebarActiveView === SidebarViewType.KickOff"
            class="lg:!text-14 !text-12 !px-0 lg:h-[72px] h-[50px] md-h:h-[50px] uppercase"
            @click.prevent="handleTabClick(SidebarViewType.KickOff)"
          >
            Kick-Off
          </MButtonNavigation>

          <Hint
            v-model="showKickOffTabHint"
            attached-to="#kick-off-tab"
            title="Review your kick-off quiz results here"
            message="Examine the answers you gave in the kick-off quiz while watching this course to determine where you can improve."
          />
        </div>

        <Divider
          layout="vertical"
          class="m-0"
        />

        <MButtonNavigation
          :active="sidebarActiveView === SidebarViewType.Preferences"
          class="!px-2 lg:h-[72px] h-[50px] md-h:h-[50px]"
          @click.prevent="handleTabClick(SidebarViewType.Preferences)"
        >
          <FontAwesomeIcon
            icon="fa-sliders"
            class="text-16"
          />
        </MButtonNavigation>
      </div>

      <Divider class="m-0" />
    </div>

    <CoursePlaylist
      v-if="sidebarActiveView === SidebarViewType.Course"
      class="px-4 pb-4"
    />
    <CourseSidebarTabsResources v-else-if="sidebarActiveView === SidebarViewType.Resources" />
    <CourseSidebarTabsKickOff v-else-if="sidebarActiveView === SidebarViewType.KickOff" />
    <CourseSidebarTabsPreferences v-else-if="sidebarActiveView === SidebarViewType.Preferences" />
  </aside>
</template>